import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { DatePipe, Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { EMPTY, Observable, Subscription, forkJoin, of } from 'rxjs';
import { catchError, finalize, map, switchMap, tap } from 'rxjs/operators';
import { DialogConfirmacaoComponent } from '@components/dialog-confirmacao/dialog-confirmacao.component';

import { MessageService } from '@services/message.service';
import { DownloadService } from '@services/download.service';
import { PermissionService } from '@services/permission.service';
import { Permissao } from '@models/enums/permissao';
import { ComponentComAlteracoesNaoSalvas } from '../../../../shared/guards/alteracoes-nao-salvas.guard';
import { ModeloVistoriaApiService } from '../../editar-modelo-vistoria/services/modelo-vistoria-api.service';
import { ModeloVistoria } from '../../editar-modelo-vistoria/models/modelo-vistoria';
import { ComodoModelo } from '../../editar-modelo-vistoria/models/comodo-modelo';
import { ItemVistoriaModelo } from '../../editar-modelo-vistoria/models/item-vistoria-modelo';
import { SituacaoVistoria, Vistoria } from '../models/vistoria';
import { VistoriaApiService, vistoriaVazia } from '../services/vistoria-api.service';
import { FotosVistoriaService } from '../services/fotos-vistoria.service';
import { RascunhoVistoria, RascunhoVistoriaService } from '../services/rascunho-vistoria.service';

type TipoDownload = 'laudo' | 'fotos';

/** Tempo sem interação antes de gravar o rascunho local. */
const ESPERA_RASCUNHO_MS = 1000;
const CLASSE_SEM_PULL_TO_REFRESH = 'sem-pull-to-refresh';

@Component({
  selector: 'app-realizar-vistoria',
  standalone: false,
  templateUrl: './realizar-vistoria.component.html',
  styleUrl: './realizar-vistoria.component.scss',
  // Uma instância por tela: as URLs locais das fotos são revogadas quando a tela é destruída.
  providers: [FotosVistoriaService, RascunhoVistoriaService],
})
export class RealizarVistoriaComponent implements OnInit, OnDestroy, ComponentComAlteracoesNaoSalvas {

  temModelo = false;
  vistoria: Vistoria = vistoriaVazia();
  comodosModelo: ComodoModelo[] = [];
  itensModelo: ItemVistoriaModelo[] = [];

  edicaoAvancada = false;
  abaAtiva: string | number = '0';
  // A visualização renderiza ao ser criada: só existe com a aba aberta, pra não renderizar antes de a
  // vistoria carregar e pra mostrar os dados atuais a cada vez que a aba é aberta.
  readonly abaVisualizacao = '3';
  readonly abaHtml = '2';
  salvando = false;
  baixando = false;
  concluida = false;

  protected get permissaoEditarRoteiro() { return Permissao.EDITAR_ROTEIRO_VISTORIA; }
  protected get permissaoGerarRelatorio() { return Permissao.GERAR_RELATORIO_VISTORIA; }
  protected get permissaoBaixarFotos() { return Permissao.BAIXAR_FOTOS_VISTORIA; }

  // O [disableNoPermission] só desabilita no ngOnInit; o [disabled] dinâmico do mesmo botão o reabilitaria
  // quando mudasse (ex: depois de salvar), por isso a permissão também entra na condição dele.
  protected get podeGerarRelatorio(): boolean {
    return this.permissionService.has(Permissao.GERAR_RELATORIO_VISTORIA);
  }

  protected get podeBaixarFotos(): boolean {
    return this.permissionService.has(Permissao.BAIXAR_FOTOS_VISTORIA);
  }

  protected get baixarBloqueado(): boolean {
    return this.salvando || this.baixando || this.vistoria.id === 0 || this.fotosVistoriaService.processando > 0;
  }

  /** Inclui as pendentes: com alterações na tela, o Baixar salva antes, e elas viram fotos salvas. */
  protected get temFotos(): boolean {
    return this.vistoria.comodos.some(c => c.itens.some(i => i.atributos.some(a => a.fotos.length > 0)));
  }

  private modelo: ModeloVistoria | null = null;

  // Estado da tela no último carregar/salvar; comparado pra saber se há alterações não salvas (ver
  // AlteracoesNaoSalvasGuard). Foto pendente entra no JSON, então também conta como alteração.
  private snapshotSalvo = '';

  // Só depois de a tela carregar e de decidir o rascunho que havia: antes disso, gravar levaria o estado do
  // servidor por cima dele. Desligado na vistoria nova (sem id) e na concluída.
  private rascunhoAtivo = false;
  private timerRascunho?: ReturnType<typeof setTimeout>;
  private gravarRascunhoSubscription?: Subscription;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private messageService: MessageService,
    private dialog: MatDialog,
    private modeloVistoriaApiService: ModeloVistoriaApiService,
    private vistoriaApiService: VistoriaApiService,
    private downloadService: DownloadService,
    private permissionService: PermissionService,
    private datePipe: DatePipe,
    public fotosVistoriaService: FotosVistoriaService,
    private rascunhoVistoriaService: RascunhoVistoriaService,
  ) { }

  ngOnInit(): void {
    // Só nesta tela (no styles.scss afetaria o sistema todo): arrastar pra baixo no topo recarregaria a página.
    document.documentElement.classList.add(CLASSE_SEM_PULL_TO_REFRESH);
    this.gravarRascunhoSubscription = this.rascunhoVistoriaService.gravarAgora$.subscribe(() => this.gravarRascunho());

    const id = Number(this.route.snapshot.params['id']);
    if (id) {
      this.carregarVistoria(id);
      return;
    }

    const modeloId = Number(this.route.snapshot.queryParams['modeloId']);
    if (modeloId) this.iniciarComModelo(modeloId);
    else this.iniciarSemModelo();
  }

  /** Só roda em navegação dentro do sistema, que passa pelo AlteracoesNaoSalvasGuard: ou salvou, ou a pessoa
   * escolheu sair sem salvar. O Chrome encerrado não chega aqui — é o caso que o rascunho cobre. */
  ngOnDestroy(): void {
    document.documentElement.classList.remove(CLASSE_SEM_PULL_TO_REFRESH);
    clearTimeout(this.timerRascunho);
    this.gravarRascunhoSubscription?.unsubscribe();
    if (this.rascunhoAtivo) this.rascunhoVistoriaService.apagar().catch(() => { });
  }

  /** Toda interação com a tela passa por um destes eventos, inclusive nos diálogos (fora do componente, por
   * isso no document). Grava o rascunho quando a pessoa para de mexer. */
  @HostListener('document:input')
  @HostListener('document:change')
  @HostListener('document:click')
  @HostListener('document:keyup')
  public agendarRascunho(): void {
    clearTimeout(this.timerRascunho);
    this.timerRascunho = setTimeout(() => this.gravarRascunho(), ESPERA_RASCUNHO_MS);
  }

  /** Trocar de app (ex: abrir a câmera) pode ser a última chance antes de o Android encerrar o Chrome. */
  @HostListener('document:visibilitychange')
  public onVisibilidadeAlterada(): void {
    if (document.visibilityState === 'hidden') this.gravarRascunho();
  }

  @HostListener('window:pagehide')
  public onPageHide(): void {
    this.gravarRascunho();
  }

  public possuiAlteracoesNaoSalvas(): boolean {
    // Snapshot vazio = a tela ainda está carregando, não há o que perder.
    return !this.concluida && this.snapshotSalvo !== '' && JSON.stringify(this.vistoria) !== this.snapshotSalvo;
  }

  public salvarAsync(): Observable<boolean> {
    return this.executarSalvar();
  }

  public alternarEdicaoAvancada(): void {
    this.edicaoAvancada = !this.edicaoAvancada;
    if (!this.edicaoAvancada && this.abaAtiva === this.abaHtml) this.abaAtiva = '0';
  }

  public voltar(): void {
    this.router.navigate(['/vistoria']);
  }

  private iniciarComModelo(modeloId: number): void {
    forkJoin({
      modelo: this.modeloVistoriaApiService.getById(modeloId),
      template: this.modeloVistoriaApiService.getTemplateHtml(modeloId),
    }).subscribe(({ modelo, template }) => {
      this.vistoria.modeloVistoriaId = modeloId;
      this.vistoria.campos = modelo.campos.map(c => ({ id: 0, nome: c.nome, tagHtmlPdf: c.tagHtmlPdf, valor: c.valorPadrao ?? '' }));
      this.vistoria.templateHtml = template.html;
      this.aplicarSugestoesDoModelo(modelo);
      this.snapshotSalvo = JSON.stringify(this.vistoria);
    });
  }

  private iniciarSemModelo(): void {
    this.vistoriaApiService.getTemplateHtmlPadrao().subscribe(template => {
      this.vistoria.templateHtml = template.html;
      this.snapshotSalvo = JSON.stringify(this.vistoria);
    });
  }

  private carregarVistoria(id: number): void {
    forkJoin({
      vistoria: this.vistoriaApiService.getById(id),
      template: this.vistoriaApiService.getTemplateHtml(id),
    }).subscribe(({ vistoria, template }) => {
      vistoria.templateHtml = template.html;
      this.aplicarVistoria(vistoria);

      if (!vistoria.modeloVistoriaId) {
        this.verificarRascunho();
        return;
      }
      this.modeloVistoriaApiService.getById(vistoria.modeloVistoriaId).subscribe(modelo => {
        this.aplicarSugestoesDoModelo(modelo);
        this.aplicarItensSugeridos();
        this.snapshotSalvo = JSON.stringify(this.vistoria);
        this.verificarRascunho();
      });
    });
  }

  /** Com a tela já carregada (snapshotSalvo final). O rascunho só serve se a vistoria não foi salva em outro
   * lugar depois dele: compara a data de modificação do servidor, sem depender do relógio do aparelho. */
  private async verificarRascunho(): Promise<void> {
    this.rascunhoVistoriaService.iniciar(this.vistoria.id);
    const rascunho = await this.rascunhoVistoriaService.carregar().catch(() => null);

    if (this.concluida) {
      if (rascunho) await this.rascunhoVistoriaService.apagar().catch(() => { });
      return;
    }

    if (rascunho && rascunho.dataModificacaoServidor !== this.vistoria.dataModificacao) {
      await this.rascunhoVistoriaService.apagar().catch(() => { });
      this.messageService.showToastWarning(
        `A vistoria foi alterada em outro lugar; as alterações locais de ${this.formatarDataRascunho(rascunho)} foram descartadas`);
    } else if (rascunho) {
      this.perguntarRestaurarRascunho(rascunho);
      return;
    }

    this.rascunhoAtivo = true;
  }

  private perguntarRestaurarRascunho(rascunho: RascunhoVistoria): void {
    const dialogRef = this.dialog.open(DialogConfirmacaoComponent, {
      width: '467px',
      panelClass: 'no-padding-dialog',
      disableClose: true,
    });
    dialogRef.componentInstance.titulo = 'Alterações não salvas';
    dialogRef.componentInstance.mensagem =
      `Há alterações não salvas desta vistoria, de ${this.formatarDataRascunho(rascunho)}, guardadas neste aparelho.`;
    dialogRef.componentInstance.textoBtnConfirmacao = 'Restaurar';
    dialogRef.componentInstance.textoBtnCancelar = 'Descartar';
    dialogRef.componentInstance.acaoConfirmacao = () => this.restaurarRascunho(rascunho);
    dialogRef.componentInstance.acaoCancelar = () => this.rascunhoVistoriaService.apagar().catch(() => { });
    dialogRef.afterClosed().subscribe(() => { this.rascunhoAtivo = true; });
  }

  /** O snapshotSalvo continua o do servidor: o que foi restaurado conta como alteração não salva. Itens novos
   * voltam com id 0, e o próximo Salvar os cria. */
  private restaurarRascunho(rascunho: RascunhoVistoria): void {
    for (const comodo of rascunho.vistoria.comodos)
      for (const item of comodo.itens)
        for (const atributo of item.atributos)
          for (const foto of atributo.fotos)
            if (foto.arquivo) this.fotosVistoriaService.registrarFotoPendente(foto.chave, foto.arquivo);

    this.vistoria = rascunho.vistoria;
    this.aplicarItensSugeridos();
  }

  private formatarDataRascunho(rascunho: RascunhoVistoria): string {
    return this.datePipe.transform(rascunho.data, 'dd/MM HH:mm') ?? '';
  }

  /** Sem alterações em relação ao salvo, não há o que guardar: apaga o que houver. */
  private gravarRascunho(): void {
    clearTimeout(this.timerRascunho);
    if (!this.rascunhoAtivo || this.concluida) return;

    const json = JSON.stringify(this.vistoria);
    const operacao = json === this.snapshotSalvo
      ? this.rascunhoVistoriaService.apagar()
      : this.rascunhoVistoriaService.gravar(this.vistoria, json);
    // Sem espaço no aparelho ou IndexedDB indisponível (ex: algumas abas anônimas): a tela segue sem rascunho.
    operacao.catch(() => { });
  }

  /** Coloca na tela uma vistoria vinda da API (ao carregar ou devolvida pelo salvar). Os cômodos acham os
   * itens sugeridos pelo comodoModeloId; o templateHtml já tem que estar preenchido. */
  private aplicarVistoria(vistoria: Vistoria): void {
    this.vistoria = vistoria;
    this.concluida = vistoria.situacao === SituacaoVistoria.Concluida;
    this.aplicarItensSugeridos();
    this.snapshotSalvo = JSON.stringify(this.vistoria);
  }

  private aplicarItensSugeridos(): void {
    if (!this.modelo) return;
    const modelo = this.modelo;
    this.vistoria.comodos.forEach(comodo =>
      comodo.itensModelo = modelo.comodos.find(c => c.id === comodo.comodoModeloId)?.itens ?? []);
  }

  private aplicarSugestoesDoModelo(modelo: ModeloVistoria): void {
    this.temModelo = true;
    this.modelo = modelo;
    this.comodosModelo = modelo.comodos;
    this.itensModelo = modelo.itens;
  }

  public salvarClick(): void {
    this.executarSalvar().subscribe();
  }

  /** Salva (com as fotos pendentes) e aplica a vistoria devolvida, sem recarregar a página: o que estava
   * aberto continua aberto e, se falhar, nada muda na tela (as fotos continuam pendentes). */
  private executarSalvar(): Observable<boolean> {
    const erro = this.validar();
    if (erro) {
      this.messageService.showToastWarning(erro);
      return of(false);
    }

    this.salvando = true;
    return this.salvarNaApi().pipe(
      tap(() => this.messageService.showToastSuccess('Vistoria salva com sucesso')),
      map(() => true),
      catchError(() => of(false)),
      finalize(() => { this.salvando = false; }),
    );
  }

  private salvarNaApi(): Observable<Vistoria> {
    const novaVistoria = this.vistoria.id === 0;
    const chamada$ = novaVistoria
      ? this.vistoriaApiService.criar(this.vistoria)
      : this.vistoriaApiService.editar(this.vistoria.id, this.vistoria);

    return chamada$.pipe(tap(salva => {
      salva.templateHtml = this.vistoria.templateHtml;
      this.aplicarVistoria(salva);
      // Troca a URL sem navegar: o componente não é recriado e nada da tela se perde.
      if (novaVistoria) {
        this.location.replaceState(`/vistoria/realizar/${salva.id}`);
        this.rascunhoVistoriaService.iniciar(salva.id);
        this.rascunhoAtivo = true;
      } else {
        this.rascunhoVistoriaService.apagar().catch(() => { });
      }
    }));
  }

  /** O laudo e as fotos baixados usam os dados salvos: com alterações na tela, pergunta antes e salva
   * primeiro (cancelar não baixa). */
  public baixarClick(tipo: TipoDownload): void {
    if (!this.possuiAlteracoesNaoSalvas()) {
      this.baixar(tipo);
      return;
    }

    const dialogRef = this.dialog.open(DialogConfirmacaoComponent, {
      width: '467px',
      panelClass: 'no-padding-dialog',
      disableClose: true,
    });
    dialogRef.componentInstance.titulo = tipo === 'laudo' ? 'Baixar laudo' : 'Baixar fotos';
    dialogRef.componentInstance.mensagem =
      `Há alterações não salvas. A vistoria será salva antes de baixar ${tipo === 'laudo' ? 'o laudo' : 'as fotos'}.`;
    dialogRef.componentInstance.textoBtnConfirmacao = 'Salvar e baixar';
    dialogRef.componentInstance.acaoConfirmacao = () =>
      this.executarSalvar().subscribe(salvou => { if (salvou) this.baixar(tipo); });
  }

  private baixar(tipo: TipoDownload): void {
    if (tipo === 'laudo') this.baixarLaudo();
    else this.baixarFotos();
  }

  private baixarLaudo(): void {
    this.baixando = true;
    const nomeArquivo = `laudo-vistoria-${this.vistoria.id}.pdf`;
    this.vistoriaApiService.gerarPdf(this.vistoria.id).pipe(
      finalize(() => { this.baixando = false; }),
    ).subscribe(blob =>
      this.downloadService.downloadFile(new File([blob], nomeArquivo, { type: 'application/pdf' }), nomeArquivo));
  }

  /** Confere antes quais fotos estão sem arquivo no servidor: o ZIP vem sem elas (listadas no
   * fotos-com-erro.txt dentro dele), e o aviso aqui garante que alguém perceba. */
  private baixarFotos(): void {
    this.baixando = true;
    const nomeArquivo = `fotos-vistoria-${this.vistoria.id}.zip`;
    this.vistoriaApiService.verificarFotos(this.vistoria.id).pipe(
      switchMap(verificacao => {
        const comErro = verificacao.fotosComErro.length;
        if (verificacao.total === 0) {
          this.messageService.showToastWarning('A vistoria não tem fotos');
          return EMPTY;
        }
        if (comErro === verificacao.total) {
          this.messageService.showToastError('Nenhuma foto da vistoria pôde ser lida no servidor');
          return EMPTY;
        }
        if (comErro > 0) {
          this.messageService.showToastWarning(comErro === 1
            ? '1 foto não pôde ser incluída — ver fotos-com-erro.txt no arquivo'
            : `${comErro} fotos não puderam ser incluídas — ver fotos-com-erro.txt no arquivo`);
        }
        return this.vistoriaApiService.baixarFotos(this.vistoria.id);
      }),
      finalize(() => { this.baixando = false; }),
    ).subscribe(blob =>
      this.downloadService.downloadFile(new File([blob], nomeArquivo, { type: 'application/zip' }), nomeArquivo));
  }

  public concluirClick(): void {
    const erro = this.validar();
    if (erro) {
      this.messageService.showToastWarning(erro);
      return;
    }

    const dialogRef = this.dialog.open(DialogConfirmacaoComponent, {
      width: '467px',
      panelClass: 'no-padding-dialog',
      disableClose: true,
    });
    dialogRef.componentInstance.titulo = 'Concluir vistoria';
    dialogRef.componentInstance.mensagem = 'As alterações na tela serão salvas e, depois de concluída, a vistoria não poderá mais ser editada.';
    dialogRef.componentInstance.textoBtnConfirmacao = 'Concluir';
    dialogRef.componentInstance.acaoConfirmacao = () => this.concluir();
  }

  private concluir(): void {
    this.salvando = true;
    this.salvarNaApi().pipe(
      switchMap(() => this.vistoriaApiService.concluir(this.vistoria.id)),
      finalize(() => { this.salvando = false; }),
    ).subscribe(() => {
      this.messageService.showToastSuccess('Vistoria concluída com sucesso');
      this.vistoria.situacao = SituacaoVistoria.Concluida;
      this.aplicarVistoria(this.vistoria);
      this.rascunhoAtivo = false;
      this.rascunhoVistoriaService.apagar().catch(() => { });
    });
  }

  private validar(): string | null {
    // Os botões já ficam desabilitados; isto cobre o "Salvar" do aviso de saída (AlteracoesNaoSalvasGuard).
    if (this.fotosVistoriaService.processando > 0) return 'Aguarde o processamento das fotos';
    if (!this.vistoria.endereco?.trim()) return 'Endereço da vistoria não pode ser vazio';
    if (this.vistoria.campos.some(c => !c.nome?.trim())) return 'Todos os campos do relatório precisam ter um nome';
    if (this.vistoria.comodos.some(c => !c.nome?.trim())) return 'Todos os cômodos precisam ter um nome';

    const itens = this.vistoria.comodos.flatMap(c => c.itens);
    if (itens.some(i => !i.nome?.trim())) return 'Todos os itens precisam ter um nome';
    if (itens.some(i => i.atributos.some(a => !a.nome?.trim()))) return 'Todos os atributos precisam ter um nome';

    return null;
  }
}
